import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import { api, formatApiErrorDetail } from "../lib/api";
import jsPDF from "jspdf";
import {
  ArrowLeft, Download, Plus, Trash2, GripVertical, Save, BookOpenCheck,
  Sparkles, Loader, Pencil, X, Check, MessageSquareQuote, Mail,
} from "lucide-react";
import EmailReportModal from "../components/EmailReportModal";
import { RatingBadge } from "../components/reports/ScoreAnalysis";
import { ChevronRight } from "lucide-react";

const CATEGORY_COLORS = {
  behavioral: "#1E40AF",
  technical: "#7C3AED",
  situational: "#FF9800",
  motivation: "#4CAF50",
  leadership: "#0E5A4A",
  cultural: "#F4511E",
};

const FRAMEWORKS = ["STAR", "SOAR", "CARL", "PAR"];

function uid() { return Math.random().toString(36).slice(2, 11); }

export default function PlaybookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pb, setPb] = useState(null);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [frameOpen, setFrameOpen] = useState(null); // question_id when modal open
  const [framing, setFraming] = useState(false);
  const [frameError, setFrameError] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/playbooks/${id}`);
      setPb(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateField = (patch) => {
    setPb({ ...pb, ...patch });
    setDirty(true);
  };

  const updateQuestion = (qid, patch) => {
    const next = (pb.questions || []).map((q) => (q.id === qid ? { ...q, ...patch } : q));
    setPb({ ...pb, questions: next });
    setDirty(true);
  };

  const addBullet = (qid) => {
    const next = (pb.questions || []).map((q) => (q.id === qid
      ? { ...q, bullets: [...(q.bullets || []), { id: uid(), text: "", source: "user" }] }
      : q
    ));
    setPb({ ...pb, questions: next });
    setDirty(true);
  };

  const removeBullet = (qid, bid) => {
    const next = (pb.questions || []).map((q) => (q.id === qid
      ? { ...q, bullets: (q.bullets || []).filter((b) => b.id !== bid) }
      : q
    ));
    setPb({ ...pb, questions: next });
    setDirty(true);
  };

  const updateBullet = (qid, bid, text) => {
    const next = (pb.questions || []).map((q) => (q.id === qid
      ? {
          ...q,
          bullets: (q.bullets || []).map((b) => (b.id === bid ? { ...b, text } : b)),
        }
      : q
    ));
    setPb({ ...pb, questions: next });
    setDirty(true);
  };

  const addQuestion = () => {
    const newQ = {
      id: uid(),
      question: "New question — click to edit",
      source: "user",
      category: "user",
      bullets: [{ id: uid(), text: "", source: "user" }],
    };
    setPb({ ...pb, questions: [...(pb.questions || []), newQ] });
    setDirty(true);
  };

  const removeQuestion = (qid) => {
    if (!window.confirm("Remove this question from the playbook?")) return;
    setPb({ ...pb, questions: (pb.questions || []).filter((q) => q.id !== qid) });
    setDirty(true);
  };

  const moveQuestion = (qid, dir) => {
    const qs = [...(pb.questions || [])];
    const idx = qs.findIndex((q) => q.id === qid);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= qs.length) return;
    [qs[idx], qs[target]] = [qs[target], qs[idx]];
    setPb({ ...pb, questions: qs });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch(`/playbooks/${id}`, {
        title: pb.title,
        company_name: pb.company_name,
        job_title: pb.job_title,
        questions: pb.questions,
      });
      setPb(data);
      setDirty(false);
      setEditing(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const frameAnswer = async (qid, framework) => {
    setFraming(true);
    setFrameError("");
    try {
      const { data } = await api.post(`/playbooks/${id}/questions/${qid}/frame`, { framework });
      const next = (pb.questions || []).map((q) => (q.id === qid
        ? { ...q, framework_answer: data.framework_answer }
        : q
      ));
      setPb({ ...pb, questions: next });
    } catch (e) {
      setFrameError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setFraming(false);
    }
  };

  const downloadPdf = () => {
    if (!pb) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 50;
    const maxWidth = 612 - margin * 2;
    let y = margin;
    const writeLine = (text, opts = {}) => {
      const size = opts.size || 11;
      const bold = !!opts.bold;
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      const lines = doc.splitTextToSize(text || "", maxWidth);
      lines.forEach((ln) => {
        if (y > 760) { doc.addPage(); y = margin; }
        doc.text(ln, margin, y);
        y += size * 1.3;
      });
    };
    writeLine((pb.title || "Interview Playbook").toUpperCase(), { size: 18, bold: true });
    if (pb.job_title || pb.company_name) {
      writeLine(`${pb.job_title || ""}${pb.company_name ? "  ·  " + pb.company_name : ""}`, { size: 10 });
    }
    y += 8;
    (pb.questions || []).forEach((q, i) => {
      writeLine(`${i + 1}. ${q.question || ""}`, { size: 12, bold: true });
      (q.bullets || []).forEach((b) => {
        if (b.text) writeLine(`   • ${b.text}`);
      });
      if (q.framework_answer) {
        y += 4;
        writeLine(`   ${q.framework_answer.kind || "STAR"} draft:`, { bold: true, size: 10 });
        ["situation", "task", "action", "result", "obstacles", "actions", "results", "context", "learning", "problem"].forEach((k) => {
          if (q.framework_answer[k]) {
            writeLine(`     ${k.toUpperCase()}: ${q.framework_answer[k]}`, { size: 10 });
          }
        });
      }
      y += 8;
    });
    const fname = `playbook-${(pb.job_title || "interview").replace(/[^a-z0-9]+/gi, "-")}.pdf`;
    doc.save(fname);
  };

  if (error && !pb) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="playbook-error">
          <ResmoMascot state="hungry" size={180} />
          <div className="font-display text-3xl mt-4 uppercase">Couldn't load that playbook</div>
          <p className="font-mono-rm text-sm mt-2">{error}</p>
          <button onClick={() => navigate("/playbooks")} className="rm-btn rm-btn-primary mt-6">
            Back to playbooks
          </button>
        </div>
      </div>
    );
  }

  if (!pb) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="playbook-loading">
          <ResmoMascot state="digesting" size={180} />
          <div className="font-display text-3xl mt-4 uppercase">Loading playbook...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rm-paper pb-20">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <Link to="/playbooks" className="rm-tag mb-3" data-testid="back-to-playbooks-tag">
          <ArrowLeft size={14} /> All playbooks
        </Link>

        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="flex-1 min-w-[240px]">
            {editing ? (
              <input
                type="text"
                value={pb.title || ""}
                onChange={(e) => updateField({ title: e.target.value })}
                className="rm-input font-display text-2xl uppercase"
                data-testid="playbook-title-input"
                style={{ width: "100%" }}
              />
            ) : (
              <h1
                className="font-display text-3xl md:text-4xl uppercase leading-tight"
                data-testid="playbook-title"
              >
                {pb.title || "Untitled"}
              </h1>
            )}
            <div
              className="font-mono-rm text-xs uppercase tracking-widest mt-2"
              style={{ color: "#475569" }}
            >
              {pb.job_title || ""}
              {pb.company_name ? `  ·  ${pb.company_name}` : ""}
              {(pb.questions || []).length ? `  ·  ${pb.questions.length} questions` : ""}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="playbook-actions">
            <button
              type="button"
              onClick={() => setEmailOpen(true)}
              className="rm-btn rm-btn-secondary text-xs px-3 py-2"
              data-testid="playbook-email-btn"
            >
              <Mail size={14} /> Email
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              className="rm-btn rm-btn-secondary text-xs px-3 py-2"
              data-testid="playbook-download-pdf-btn"
            >
              <Download size={14} /> PDF
            </button>
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="rm-btn rm-btn-primary text-xs px-3 py-2"
                  data-testid="playbook-save-btn"
                >
                  {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => { load(); setEditing(false); setDirty(false); }}
                  disabled={saving}
                  className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                  data-testid="playbook-cancel-edit-btn"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rm-btn rm-btn-dark text-xs px-3 py-2"
                data-testid="playbook-edit-btn"
              >
                <Pencil size={14} /> Edit
              </button>
            )}
          </div>
        </div>

        {error && (
          <div
            className="mb-4 p-3 rounded font-mono-rm text-sm"
            style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
            data-testid="playbook-page-error"
          >
            {error}
          </div>
        )}

        {dirty && !saving && (
          <div
            className="mb-4 p-2 text-xs font-mono-rm rounded"
            style={{ background: "#FEF3C7", border: "2px dashed #F59E0B" }}
            data-testid="playbook-dirty"
          >
            Unsaved changes — click <strong>Save</strong> to keep them.
          </div>
        )}

        {/* Scannable question index — tap a row to jump to its full card */}
        {(pb.questions || []).length > 1 && (
          <div className="rm-card-static p-4 mb-4" style={{ background: "white" }} data-testid="playbook-question-index">
            <div className="rm-label mb-2">Question map · tap to jump</div>
            <div className="space-y-1.5">
              {(pb.questions || []).map((q, i) => (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => {
                    const el = document.querySelector(`[data-testid="playbook-q-${i}"]`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="w-full flex items-center gap-3 p-2.5 text-left rounded-lg transition-colors hover:bg-slate-50"
                  style={{ border: "1.5px solid #E2E8F0" }}
                  data-testid={`playbook-index-row-${i}`}
                >
                  <RatingBadge
                    label={(q.category || q.source || "question").slice(0, 12)}
                    color={CATEGORY_COLORS[(q.category || "").toLowerCase()] || "#64748B"}
                  />
                  <span className="flex-1 min-w-0 text-[13px] font-bold leading-snug line-clamp-1" style={{ color: "#0F1B2D" }}>
                    {i + 1}. {q.question}
                  </span>
                  <ChevronRight size={15} className="flex-shrink-0" style={{ color: "#94A3B8" }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-4" data-testid="playbook-questions">
          {(pb.questions || []).map((q, i) => (
            <QuestionCard
              key={q.id}
              index={i}
              question={q}
              editing={editing}
              onChangeQuestion={(text) => updateQuestion(q.id, { question: text })}
              onAddBullet={() => addBullet(q.id)}
              onRemoveBullet={(bid) => removeBullet(q.id, bid)}
              onUpdateBullet={(bid, text) => updateBullet(q.id, bid, text)}
              onRemoveQuestion={() => removeQuestion(q.id)}
              onMoveUp={() => moveQuestion(q.id, -1)}
              onMoveDown={() => moveQuestion(q.id, 1)}
              onOpenFrame={() => setFrameOpen(q.id)}
              isLast={i === (pb.questions || []).length - 1}
            />
          ))}
        </div>

        {editing && (
          <button
            type="button"
            onClick={addQuestion}
            className="rm-btn rm-btn-ghost text-xs mt-4"
            data-testid="playbook-add-question-btn"
          >
            <Plus size={14} /> Add a question
          </button>
        )}

        {(pb.questions || []).length === 0 && (
          <div
            className="rm-card-static p-8 text-center mt-4"
            data-testid="playbook-empty-questions"
          >
            <ResmoMascot state="hungry" size={120} />
            <div className="font-display text-xl uppercase mt-2 mb-1">No questions yet</div>
            <p className="text-sm" style={{ color: "#475569" }}>
              Add your first question to get started.
            </p>
          </div>
        )}
      </div>

      <FrameModal
        open={!!frameOpen}
        question={(pb.questions || []).find((q) => q.id === frameOpen)}
        framing={framing}
        error={frameError}
        onClose={() => { setFrameOpen(null); setFrameError(""); }}
        onFrame={(framework) => frameAnswer(frameOpen, framework)}
      />

      <EmailReportModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        kindLabel="Interview Playbook"
        sharePath={`/playbooks/${id}/share`}
        emailPath={`/playbooks/${id}/email`}
        publicPath="/playbook/share/"
      />
    </div>
  );
}

function QuestionCard({
  index, question, editing,
  onChangeQuestion, onAddBullet, onRemoveBullet, onUpdateBullet,
  onRemoveQuestion, onMoveUp, onMoveDown, onOpenFrame,
}) {
  const fa = question.framework_answer;
  return (
    <div
      className="rm-card-static p-5"
      style={{ background: "white" }}
      data-testid={`playbook-q-${index}`}
    >
      <div className="flex items-start gap-2 mb-3">
        <div
          className="font-display text-lg flex-shrink-0"
          style={{ color: "#FF6A00", width: 28 }}
        >
          {index + 1}.
        </div>
        <div className="flex-1">
          {editing ? (
            <textarea
              value={question.question || ""}
              onChange={(e) => onChangeQuestion(e.target.value)}
              className="rm-textarea text-base font-bold"
              style={{ minHeight: 40 }}
              data-testid={`playbook-q-${index}-text`}
            />
          ) : (
            <div className="text-base font-bold" data-testid={`playbook-q-${index}-text`}>
              {question.question}
            </div>
          )}
          {question.source && (
            <span
              className="rm-tag mt-1"
              style={{
                background: question.source === "company" ? "#FEF3C7"
                          : question.source === "user" ? "#ECFDF5" : "white",
              }}
            >
              {question.source}
            </span>
          )}
        </div>
        {editing && (
          <div className="flex flex-col gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={onMoveUp}
              className="rm-btn rm-btn-ghost text-xs px-1.5 py-0.5"
              title="Move up"
              data-testid={`playbook-q-${index}-move-up`}
            >
              ↑
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              className="rm-btn rm-btn-ghost text-xs px-1.5 py-0.5"
              title="Move down"
              data-testid={`playbook-q-${index}-move-down`}
            >
              ↓
            </button>
            <button
              type="button"
              onClick={onRemoveQuestion}
              className="rm-btn rm-btn-ghost text-xs px-1.5 py-0.5"
              title="Remove question"
              data-testid={`playbook-q-${index}-remove`}
            >
              <Trash2 size={11} />
            </button>
          </div>
        )}
      </div>

      <ul className="space-y-2 ml-7" data-testid={`playbook-q-${index}-bullets`}>
        {(question.bullets || []).map((b, bi) => (
          <li key={b.id} className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-[7px]" style={{ width: 4, height: 4, borderRadius: "50%", background: "#FF6A00" }} />
            {editing ? (
              <>
                <textarea
                  value={b.text || ""}
                  onChange={(e) => onUpdateBullet(b.id, e.target.value)}
                  className="rm-textarea text-sm flex-1"
                  style={{ minHeight: 40 }}
                  data-testid={`playbook-q-${index}-b-${bi}`}
                  placeholder="Evidence from your resume, scope, numbers, or a note to self"
                />
                <button
                  type="button"
                  onClick={() => onRemoveBullet(b.id)}
                  className="rm-btn rm-btn-ghost text-xs px-1.5 py-0.5 flex-shrink-0"
                  title="Remove bullet"
                  data-testid={`playbook-q-${index}-b-${bi}-remove`}
                >
                  <X size={11} />
                </button>
              </>
            ) : (
              <span className="text-sm" data-testid={`playbook-q-${index}-b-${bi}`}>
                {b.text || <em style={{ color: "#94A3B8" }}>(empty)</em>}
              </span>
            )}
          </li>
        ))}
      </ul>

      <div className="ml-7 mt-3 flex flex-wrap gap-2">
        {editing && (
          <button
            type="button"
            onClick={onAddBullet}
            className="rm-btn rm-btn-ghost text-xs px-2 py-1"
            data-testid={`playbook-q-${index}-add-bullet`}
          >
            <Plus size={11} /> Add bullet
          </button>
        )}
        <button
          type="button"
          onClick={onOpenFrame}
          className="rm-btn rm-btn-ghost text-xs px-2 py-1"
          data-testid={`playbook-q-${index}-frame-btn`}
        >
          <MessageSquareQuote size={11} /> {fa ? `Re-frame (${fa.kind || "STAR"})` : "Frame answer (1 credit)"}
        </button>
      </div>

      {fa && (
        <div
          className="ml-7 mt-3 p-3 rounded-md text-sm"
          style={{ background: "#FFFBEB", border: "1.5px solid #F59E0B" }}
          data-testid={`playbook-q-${index}-framed`}
        >
          <div
            className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 mb-2"
            style={{ color: "#B45309", fontWeight: 700 }}
          >
            <Sparkles size={11} /> · {fa.kind || "STAR"} draft
          </div>
          {["situation", "task", "action", "result",
            "obstacles", "actions", "results",
            "context", "learning",
            "problem"].map((k) => (
            fa[k] ? (
              <div key={k} className="mb-1.5">
                <strong className="uppercase font-mono-rm text-[10px]" style={{ color: "#475569" }}>
                  {k}:
                </strong>{" "}
                {fa[k]}
              </div>
            ) : null
          ))}
          {fa.spoken_version && (
            <div
              className="mt-2 pt-2"
              style={{ borderTop: "1.5px solid #F59E0B" }}
            >
              <div className="rm-label mb-1">Say it out loud</div>
              <div className="italic" style={{ color: "#0F1B2D" }}>
                "{fa.spoken_version}"
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FrameModal({ open, question, framing, error, onClose, onFrame }) {
  const [framework, setFramework] = useState("STAR");
  if (!open || !question) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto"
      style={{ background: "rgba(15, 27, 45, 0.55)" }}
      onClick={() => !framing && onClose()}
      data-testid="frame-modal"
    >
      <div
        className="rm-card-static p-6 md:p-7 w-full max-w-md"
        style={{ background: "#FFF2EC" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="rm-tag mb-2">
              <BookOpenCheck size={12} /> Frame an answer
            </div>
            <h2 className="font-display text-xl uppercase leading-tight">
              Pick a <span className="rm-text-orange">framework.</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={() => !framing && onClose()}
            className="rm-btn rm-btn-ghost text-xs px-2 py-2"
            data-testid="frame-modal-close"
          >
            <X size={14} />
          </button>
        </div>
        <p className="text-sm mb-4" style={{ color: "#475569" }}>
          Resmo will turn your bullets + resume into a structured answer using your chosen
          storytelling framework. Costs <strong>1 credit</strong>.
        </p>
        <div className="mb-4">
          <div className="rm-label mb-2">Question</div>
          <div className="p-3 rounded font-bold text-sm" style={{ background: "white", border: "3px solid #0F1B2D" }}>
            {question.question}
          </div>
        </div>
        <div className="mb-4">
          <div className="rm-label mb-2">Framework</div>
          <div className="grid grid-cols-2 gap-2">
            {FRAMEWORKS.map((fw) => {
              const active = framework === fw;
              return (
                <button
                  type="button"
                  key={fw}
                  onClick={() => setFramework(fw)}
                  disabled={framing}
                  className="rm-btn text-xs"
                  style={{
                    background: active ? "#0F1B2D" : "white",
                    color: active ? "white" : "#0F1B2D",
                    border: "3px solid #0F1B2D",
                    padding: "10px",
                    justifyContent: "center",
                  }}
                  data-testid={`frame-pick-${fw}`}
                >
                  {fw}
                  {active && <Check size={12} />}
                </button>
              );
            })}
          </div>
          <div className="font-mono-rm text-xs mt-2" style={{ color: "#475569" }}>
            {framework === "STAR" && "Situation · Task · Action · Result — the classic."}
            {framework === "SOAR" && "Situation · Obstacles · Actions · Results — strong for adversity."}
            {framework === "CARL" && "Context · Action · Result · Learning — great for growth stories."}
            {framework === "PAR" && "Problem · Action · Result — concise, exec-friendly."}
          </div>
        </div>
        {error && (
          <div
            className="p-3 mb-4 rounded font-mono-rm text-sm"
            style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
            data-testid="frame-modal-error"
          >
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={() => onFrame(framework)}
          disabled={framing}
          className="rm-btn rm-btn-primary w-full"
          data-testid="frame-submit-btn"
        >
          {framing ? <Loader size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {framing ? "Resmo is drafting..." : `Frame answer with ${framework}`}
        </button>
      </div>
    </div>
  );
}

// Silence the unused-warning for GripVertical (kept available for future drag-to-reorder).
void GripVertical;
